import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE from '../apiBase';

const ProjectSelection = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [maxProjects, setMaxProjects] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const paymentId = urlParams.get('payment_id');
        const maxProjectsParam = urlParams.get('max_projects');
        
        if (!paymentId || !maxProjectsParam) {
          setError('Missing required parameters. Please try again.');
          return;
        }

        setMaxProjects(parseInt(maxProjectsParam));

        // Authorization handled by axios interceptor (Firebase ID token)

        // Fetch user's projects
        const response = await axios.get(`${API_BASE}/api/projects`);
        setProjects(response.data);
        
        // Pre-select projects up to the limit
        const projectsToSelect = response.data.slice(0, parseInt(maxProjectsParam));
        setSelectedProjects(projectsToSelect.map(p => p.id));

      } catch (error) {
        console.error('Error fetching projects:', error);
        setError('Failed to load projects. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        if (prev.length < maxProjects) {
          return [...prev, projectId];
        }
        return prev;
      }
    });
  };

  const handleSaveSelection = async () => {
    if (selectedProjects.length === 0) {
      setError('Please select at least one project to keep active.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment_id');

      // Link selected projects to the new subscription
      await axios.post(`${API_BASE}/api/project-subscription/link-projects-downgrade`, {
        project_ids: selectedProjects,
        payment_id: paymentId
      });

      // Redirect to projects page with success message
      window.location.href = '/app/projects?selection=success';
    } catch (error) {
      console.error('Error saving project selection:', error);
      setError(error.response?.data?.error || 'Failed to save project selection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedProjects.length === 0) {
      setError('Please select at least one project to keep active.');
      return;
    }
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.href = '/app/plans'}>
          Back to Plans
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Select Projects to Keep Active
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          Your plan allows a maximum of <strong>{maxProjects}</strong> active projects.
          Select which projects you want to keep active.
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          <InfoIcon sx={{ mr: 1 }} />
          Projects not selected will be deactivated but can be reactivated if you upgrade again.
        </Alert>
      </Box>

      {/* Project Selection */}
      <Grid container spacing={3}>
        {projects.map((project) => {
          const isSelected = selectedProjects.includes(project.id);
          const isDisabled = !isSelected && selectedProjects.length >= maxProjects;
          
          return (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <Card 
                sx={{ 
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.6 : 1,
                  border: isSelected ? '2px solid #4CAF50' : '2px solid transparent',
                  '&:hover': {
                    border: isDisabled ? '2px solid transparent' : '2px solid #4CAF50',
                    transform: isDisabled ? 'none' : 'translateY(-2px)',
                    transition: 'all 0.2s ease'
                  }
                }}
                onClick={() => !isDisabled && handleProjectToggle(project.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          color="primary"
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {project.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {project.description || 'No description'}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={project.repository_type || 'Unknown'} 
                          size="small" 
                          variant="outlined" 
                        />
                        {project.jira_enabled && (
                          <Chip 
                            label="Jira Enabled" 
                            size="small" 
                            color="primary" 
                          />
                        )}
                        {project.subscription && (
                          <Chip 
                            label={project.subscription.plan_name} 
                            size="small" 
                            color="secondary" 
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Selection Summary */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Selected: {selectedProjects.length} / {maxProjects} projects
        </Typography>
        
        {selectedProjects.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <WarningIcon sx={{ mr: 1 }} />
            Please select at least one project to keep active.
          </Alert>
        )}
        
        {selectedProjects.length > maxProjects && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You can only select up to {maxProjects} projects. Please deselect some projects.
          </Alert>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => window.location.href = '/app/plans'}
          sx={{ mr: 2 }}
        >
          Back to Plans
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmSelection}
          disabled={selectedProjects.length === 0 || selectedProjects.length > maxProjects || saving}
          startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {saving ? 'Saving...' : 'Confirm Selection'}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon color="primary" />
            <Typography variant="h6">Confirm Project Selection</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You're about to activate <strong>{selectedProjects.length}</strong> projects with your new plan.
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
            The remaining {projects.length - selectedProjects.length} projects will be deactivated but can be reactivated if you upgrade again.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSelection}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectSelection; 